import _ from 'lodash'
import getConfig from 'next/config'
import mixpanel from 'mixpanel-browser'

import { District, School, User } from 'common/types'

const { publicRuntimeConfig } = getConfig()

const mixpanelToken = publicRuntimeConfig.MIXPANEL_TOKEN

export enum EVENTS {
  VISIT_RESOURCE = 'VISIT_RESOURCE',
  // This will be used as a prefix for the resource collection
  VISIT_RESOURCE_ = 'VISIT_RESOURCE_',
  // This will be used as a prefix for the collection category
  VISIT_COLLECTION_CATEGORY_ = 'VISIT_COLLECTION_CATEGORY_',
  VISIT_COURSE = 'VISIT_COURSE',
  VISIT_COURSE_MODULE = 'VISIT_COURSE_MODULE',
  COMPLETE_COURSE_MODULE = 'COMPLETE_COURSE_MODULE',
  COMPLETE_COURSE = 'COMPLETE_COURSE',
  // This will be used as a prefix for the course completion
  COMPLETE_COURSE_ = 'COMPLETE_COURSE_',
  COMPLETE_NUDGE = 'COMPLETE_NUDGE',
  PUBLISH_NUDGE_SET = 'PUBLISH_NUDGE_SET',
  UNPUBLISH_NUDGE_SET = 'UNPUBLISH_NUDGE_SET',
  COMPLETE_ROADMAP_MILESTONE = 'COMPLETE_ROADMAP_MILESTONE',
  VISIT_CURRICULUM = 'VISIT_CURRICULUM',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  START_MOOD_METER_GROUP = 'START_MOOD_METER_GROUP',
  PLOT_MOOD_METER = 'PLOT_MOOD_METER',
  PLOT_PERSONAL_MOOD_METER = 'PLOT_PERSONAL_MOOD_METER',
  START_ONBOARDING = 'START_ONBOARDING',
  COMPLETE_ONBOARDING = 'COMPLETE_ONBOARDING',
  FAVORITE_RESOURCE = 'FAVORITE_RESOURCE',
  UNFAVORITE_RESOURCE = 'UNFAVORITE_RESOURCE',
  UPLOAD_SHOWCASE = 'UPLOAD_SHOWCASE',
  LIKE_SHOWCASE_POST = 'LIKE_SHOWCASE_POST',
  CREATE_SCHOOL_CHARTER = 'CREATE_SCHOOL_CHARTER',
  ADD_EMOTION_WORDS_TO_CHARTER = 'ADD_EMOTION_WORDS_TO_CHARTER',
  ADD_EMOTION_WORDS_TO_SCHOOL_CHARTER = 'ADD_EMOTION_WORDS_TO_SCHOOL_CHARTER',
  HOW_I_WANT_TO_FEEL_FROM_ONBOARDING = 'HOW_I_WANT_TO_FEEL_FROM_ONBOARDING',
  HOW_I_WANT_TO_FEEL_FROM_PROFILE = 'HOW_I_WANT_TO_FEEL_FROM_PROFILE',

  // Explore tab
  CHANGE_EXPLORE_RESOURCE_FILTER = 'CHANGE_EXPLORE_RESOURCE_FILTER',
  CHANGE_EXPLORE_RESOURCE_SORT = 'CHANGE_EXPLORE_RESOURCE_SORT',
  USE_EXPLORE_RESOURCE_SEARCH = 'USE_EXPLORE_RESOURCE_SEARCH',
}

export const mixpanelEnabled =
  publicRuntimeConfig.SERVER_ENV === 'production' || publicRuntimeConfig.SERVER_ENV === 'staging'

// Event queue implementation
type QueuedEvent = {
  eventName: EVENTS | string
  data: Record<string, string | number>
}

// Track whether groups have been set
let readyForEventsTracking = false
const eventQueue: QueuedEvent[] = []

// Helper function to process queued events
function processEventQueue() {
  if (!readyForEventsTracking || !mixpanelEnabled) return

  eventQueue.forEach(({ eventName, data }) => {
    mixpanel.track(eventName, data)
  })

  // Clear the queue after processing
  eventQueue.length = 0
}

// Helper to determine if an event should be queued
function shouldQueueEvent(eventName: EVENTS | string): boolean {
  return (
    !readyForEventsTracking &&
    (eventName === EVENTS.VISIT_RESOURCE ||
      (typeof eventName === 'string' && eventName.startsWith(EVENTS.VISIT_RESOURCE_)))
  )
}

export default mixpanelEnabled
  ? {
      init: () => {
        mixpanel.init(mixpanelToken)
      },
      boot: (
        user: User,
        school?: Pick<School, 'created_at'>,
        district?: Pick<District, 'created_at'>,
      ) => {
        if (!mixpanelToken) return

        mixpanel.identify(user.id)
        const data = {
          user_id: user.id,
          created_at: user.created_at,
          onboarded_at: user.onboarded_at,
          status: user.status,
          name: user.name,
          email: user.email,
          role: user.role_name,
          label: user.person_type_name,
          self_reged: user.self_reged,
          grades: _.join(_.map(user.grades, 'name'), ', '),
          subjects: _.join(_.map(user.subjects, 'name'), ', '),
          emotion_words: _.join(_.map(user.emotion_words, 'name'), ', '),
          job_title: user.job_title,
        }
        mixpanel.people.set(data)
        if (user.school && user.school_id) {
          mixpanel.set_group('school_name', user.school)
          mixpanel.get_group('school_name', user.school).set({
            id: user.school_id,
            name: user.school,
            created_in_ruler_online: school?.created_at,
          })
        }

        if (user.district && user.district_id) {
          mixpanel.set_group('district_name', user.district)
          mixpanel.get_group('district_name', user.district).set({
            id: user.district_id,
            name: user.district,
            created_in_ruler_online: district?.created_at,
          })
        }

        // // Wait for 2 seconds before marking groups as set to ensure that the previous Mixpanel events have been sent
        // setTimeout(() => {
        // Mark groups as set and process any queued events
        readyForEventsTracking = true
        processEventQueue()
        // }, 2000)
      },
      reset: () => {
        mixpanel.reset()
        readyForEventsTracking = false
        eventQueue.length = 0
      },
      track: (eventName: EVENTS | string, data: Record<string, string | number>) => {
        if (shouldQueueEvent(eventName)) {
          // Queue events that depend on group information
          eventQueue.push({ eventName, data })
        } else {
          mixpanel.track(eventName, data)
        }
      },
      bootWithoutUser: () => {
        readyForEventsTracking = true
        processEventQueue()
      },
    }
  : {
      init: _.noop,
      boot: _.noop,
      reset: _.noop,
      track: _.noop,
      bootWithoutUser: _.noop,
    }
